/**
 * Audit Trail Logging System
 * 
 * Tracks all user-initiated changes for compliance and regulatory purposes
 * Supports WBC3 audit requirements and future export capabilities
 */

import { debug } from './logging';

// Storage key for audit log (confirmed with Schema GPT)
const AUDIT_KEY = 'quicklog_audit_log';

/**
 * Add a new audit entry to the log
 * @param {Object} entry - Audit entry object
 * @param {string} entry.action - Type of action ("create", "edit", "resolve", "delete", etc.)
 * @param {string} entry.module - Module name (e.g., "DefectModule", "DrillModule")
 * @param {string} entry.targetId - ID or identifier of the affected item
 * @param {string} entry.summary - Human-readable summary of the change
 * @param {Object} [entry.details] - Optional additional details about the change
 * @param {string} [entry.userId] - User ID (future auth integration)
 * @param {Object} [entry.context] - Additional context (vessel info, etc.)
 */
export function addAuditEntry(entry) {
  try {
    // Validate required fields
    if (!entry.action || !entry.module || !entry.targetId || !entry.summary) {
      debug.warn('Audit entry missing required fields:', entry);
      return false;
    }

    // Get existing audit log
    const log = getAuditLog();
    
    // Create complete audit entry
    const auditEntry = {
      id: generateAuditId(),
      timestamp: new Date().toISOString(),
      action: entry.action,
      module: entry.module,
      targetId: entry.targetId,
      summary: entry.summary,
      details: entry.details || {},
      userId: entry.userId || 'current_user', // Will be replaced with actual auth
      context: entry.context || {},
      environment: process.env.NODE_ENV || 'development'
    };

    // Add to log
    log.push(auditEntry);
    
    // Store updated log
    localStorage.setItem(AUDIT_KEY, JSON.stringify(log));
    
    debug.log('Audit entry added:', auditEntry);
    return true;
    
  } catch (error) {
    debug.error('Failed to add audit entry:', error);
    return false;
  }
}

/**
 * Retrieve the complete audit log
 * @param {Object} options - Filter options
 * @param {string} [options.module] - Filter by module
 * @param {string} [options.action] - Filter by action type
 * @param {string} [options.targetId] - Filter by target ID
 * @param {Date} [options.fromDate] - Filter from date
 * @param {Date} [options.toDate] - Filter to date
 * @param {number} [options.limit] - Limit number of results
 * @returns {Array} Array of audit entries
 */
export function getAuditLog(options = {}) {
  try {
    let log = JSON.parse(localStorage.getItem(AUDIT_KEY) || '[]');
    
    // Apply filters if provided
    if (options.module) {
      log = log.filter(entry => entry.module === options.module);
    }
    
    if (options.action) {
      log = log.filter(entry => entry.action === options.action);
    }
    
    if (options.targetId) {
      log = log.filter(entry => entry.targetId === options.targetId);
    }
    
    if (options.fromDate) {
      const fromDate = new Date(options.fromDate);
      log = log.filter(entry => new Date(entry.timestamp) >= fromDate);
    }
    
    if (options.toDate) {
      const toDate = new Date(options.toDate);
      log = log.filter(entry => new Date(entry.timestamp) <= toDate);
    }
    
    // Sort by timestamp (newest first)
    log.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    
    // Apply limit if specified
    if (options.limit && options.limit > 0) {
      log = log.slice(0, options.limit);
    }
    
    return log;
    
  } catch (error) {
    debug.error('Failed to retrieve audit log:', error);
    return [];
  }
}

/**
 * Get audit entries for a specific item
 * @param {string} targetId - ID of the item to get audit trail for
 * @param {string} [module] - Optional module filter
 * @returns {Array} Array of audit entries for the item
 */
export function getAuditTrailForItem(targetId, module = null) {
  const options = { targetId };
  if (module) options.module = module;
  
  return getAuditLog(options);
}

/**
 * Get recent audit activity
 * @param {number} limit - Number of recent entries to return (default: 10)
 * @returns {Array} Array of recent audit entries
 */
export function getRecentActivity(limit = 10) {
  return getAuditLog({ limit });
}

/**
 * Get audit statistics
 * @returns {Object} Statistics about audit log
 */
export function getAuditStatistics() {
  const log = getAuditLog();
  
  const stats = {
    totalEntries: log.length,
    moduleBreakdown: {},
    actionBreakdown: {},
    todayCount: 0,
    weekCount: 0,
    monthCount: 0
  };
  
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  
  log.forEach(entry => {
    const entryDate = new Date(entry.timestamp);
    
    // Module breakdown
    stats.moduleBreakdown[entry.module] = (stats.moduleBreakdown[entry.module] || 0) + 1;
    
    // Action breakdown
    stats.actionBreakdown[entry.action] = (stats.actionBreakdown[entry.action] || 0) + 1;
    
    // Time-based counts
    if (entryDate >= today) stats.todayCount++;
    if (entryDate >= weekAgo) stats.weekCount++;
    if (entryDate >= monthAgo) stats.monthCount++;
  });
  
  return stats;
}

/**
 * Clear the audit log (admin function)
 * @param {boolean} confirm - Confirmation flag to prevent accidental clearing
 * @returns {boolean} Success status
 */
export function clearAuditLog(confirm = false) {
  if (!confirm) {
    debug.warn('Audit log clear attempted without confirmation');
    return false;
  }
  
  try {
    localStorage.removeItem(AUDIT_KEY);
    debug.log('Audit log cleared');
    return true;
  } catch (error) {
    debug.error('Failed to clear audit log:', error);
    return false;
  }
}

/**
 * Export audit log for compliance reporting
 * @param {Object} options - Export options
 * @param {string} [options.format] - Export format ('json', 'csv')
 * @param {Object} [options.filters] - Same filters as getAuditLog
 * @returns {Object} Export data and metadata
 */
export function exportAuditLog(options = {}) {
  const { format = 'json', ...filterOptions } = options;
  const log = getAuditLog(filterOptions);
  
  const exportData = {
    exportDate: new Date().toISOString(),
    totalEntries: log.length,
    filters: filterOptions,
    environment: process.env.NODE_ENV,
    format: format,
    data: log
  };
  
  if (format === 'csv') {
    exportData.csvData = convertToCSV(log);
  }
  
  debug.log('Audit log exported:', { format, entryCount: log.length });
  return exportData;
}

/**
 * Helper function to generate unique audit IDs
 */
function generateAuditId() {
  return `audit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Helper function to convert audit log to CSV format
 */
function convertToCSV(log) {
  if (log.length === 0) return '';
  
  const headers = ['ID', 'Timestamp', 'Action', 'Module', 'Target ID', 'Summary', 'User ID', 'Environment'];
  const rows = log.map(entry => [
    entry.id,
    entry.timestamp,
    entry.action,
    entry.module,
    entry.targetId,
    entry.summary.replace(/"/g, '""'), // Escape quotes for CSV
    entry.userId,
    entry.environment
  ]);
  
  return [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\n');
}

/**
 * Predefined action types for consistency
 */
export const AUDIT_ACTIONS = {
  CREATE: 'create',
  EDIT: 'edit',
  UPDATE: 'update',
  DELETE: 'delete',
  RESOLVE: 'resolve',
  COMPLETE: 'complete',
  CANCEL: 'cancel',
  ASSIGN: 'assign',
  APPROVE: 'approve',
  REJECT: 'reject',
  EXPORT: 'export',
  IMPORT: 'import',
  VIEW: 'view',
  LOGIN: 'login',
  LOGOUT: 'logout'
};

/**
 * Predefined module names for consistency
 */
export const AUDIT_MODULES = {
  DEFECT: 'DefectModule',
  DRILL: 'DrillModule',
  EQUIPMENT: 'EquipmentModule',
  CREW: 'CrewModule',
  DASHBOARD: 'Dashboard',
  SETTINGS: 'Settings',
  EXPORT: 'ExportModule',
  AUTH: 'AuthModule'
};

// Export convenience functions
export default {
  addAuditEntry,
  getAuditLog,
  getAuditTrailForItem,
  getRecentActivity,
  getAuditStatistics,
  clearAuditLog,
  exportAuditLog,
  AUDIT_ACTIONS,
  AUDIT_MODULES
};